import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import { Spinner } from '@/components/atoms/Spinner';
import EmptyState from '@/components/molecules/EmptyState';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import SomethingHappened from '@/components/SomethingHappened';
import { useGetFishGroupLocations } from '@/domain/fishGroups';
import { formatDateTimeWorded } from '@/utils/date';

import styles from '../FishGroupDetailModal.module.scss';

interface Props {
    clientId: number;
    fishGroupId: number;
}

const ROW_HEIGHT = 44;
const PLACEHOLDER = '—';

export default function LocationsTab(props: Props) {
    const { clientId, fishGroupId } = props;
    const { t } = useTranslation('fishGroups');
    const { data: locations, isLoading, error } = useGetFishGroupLocations(clientId, fishGroupId);

    if (isLoading) return <Spinner size="lg" />;
    if (error) return <SomethingHappened />;
    if (locations.length === 0) {
        return <EmptyState title={t('detail.locations.empty')} appearance="quiet" />;
    }

    const placeholder = <span className={styles.placeholder}>{PLACEHOLDER}</span>;
    const columns: DataTableColumnDef<(typeof locations)[number]>[] = [
        {
            id: 'cage',
            header: t('detail.locations.col.cage'),
            cell: ({ row }) => row.original.cageName,
        },
        {
            id: 'location',
            header: t('detail.locations.col.location'),
            cell: ({ row }) => row.original.locationName,
        },
        {
            id: 'from',
            header: t('detail.locations.col.from'),
            cell: ({ row }) => formatDateTimeWorded(row.original.fromDate, 'minute'),
        },
        {
            id: 'to',
            header: t('detail.locations.col.to'),
            cell: ({ row }) =>
                row.original.toDate
                    ? formatDateTimeWorded(row.original.toDate, 'minute')
                    : placeholder,
        },
        {
            id: 'reason',
            header: t('detail.locations.col.reason'),
            meta: { width: '1.5fr' },
            cell: ({ row }) => row.original.reason ?? placeholder,
        },
        {
            id: 'status',
            header: t('detail.locations.col.status'),
            meta: { width: '0.6fr' },
            cell: ({ row }) =>
                row.original.toDate === null && (
                    <Badge tone="success" shape="tag">
                        {t('detail.locations.current')}
                    </Badge>
                ),
        },
    ];

    return (
        <DataTable
            data={locations}
            columns={columns}
            getRowId={(row) => String(row.id)}
            layout="grid"
            enableSorting={false}
            rowHeight={ROW_HEIGHT}
            getRowProps={() => ({ disableHover: true })}
        />
    );
}
